import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Ban, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function BlockUserButton({ username, isBlocked, onBlockChange }) {
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleBlock = async () => {
    try {
      setLoading(true);
      const method = isBlocked ? 'DELETE' : 'POST';
      const endpoint = isBlocked 
        ? `/api/social/user/${username}/unblock`
        : `/api/social/user/${username}/block`;

      const res = await fetch(`${API_URL}${endpoint}`, {
        method,
        credentials: 'include'
      });

      if (res.ok) {
        toast.success(isBlocked ? `Unblocked @${username}` : `Blocked @${username}`);
        setShowConfirm(false);
        if (onBlockChange) {
          onBlockChange(!isBlocked);
        }
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Action failed');
      }
    } catch (error) {
      toast.error('Failed to ' + (isBlocked ? 'unblock' : 'block') + ' user');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        variant={isBlocked ? "outline" : "destructive"}
        size="sm"
        onClick={() => setShowConfirm(true)}
      >
        <Ban className="h-4 w-4 mr-2" />
        {isBlocked ? 'Unblock' : 'Block'}
      </Button>

      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-500" />
              {isBlocked ? 'Unblock User?' : 'Block User?'}
            </DialogTitle>
            <DialogDescription>
              {isBlocked ? (
                <>
                  <strong>@{username}</strong> will be able to:
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>See your posts in feeds</li>
                    <li>Follow you again</li>
                    <li>Comment on your posts</li>
                  </ul>
                </>
              ) : (
                <>
                  <strong>@{username}</strong> will no longer be able to:
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>See your posts in their feed</li>
                    <li>Follow you</li>
                    <li>Comment on your posts</li>
                  </ul>
                  <p className="mt-2 text-sm">You will both automatically unfollow each other.</p>
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirm(false)}>
              Cancel
            </Button>
            <Button
              variant={isBlocked ? "default" : "destructive"}
              onClick={handleBlock}
              disabled={loading}
            >
              {loading ? 'Processing...' : (isBlocked ? 'Unblock' : 'Block User')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
